import React, { useEffect, useState } from 'react';
import {
  RiPauseFill,
  RiPencilLine,
  RiDeleteBinLine,
  RiCheckDoubleLine,
  RiLoader4Line
} from 'react-icons/ri';
import styles from './AudioItem.module.css';
import LibraryPreview from './LibraryPreview';
import wasabiService from '../services/WasabiService';
import AutoBookCover, { splitTitleAndAuthor, composeTitleAndAuthor } from './AutoBookCover';
import OfflineButton from '../offline/OfflineButton';
import PushOptIn from '../offline/PushOptIn';

// Human-readable length for the card meta: "4h 42m", "42 min". The old
// "4:42:00" timestamp format read like a wall clock, not a book length.
const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (!total) return '';
  let hours = Math.floor(total / 3600);
  let minutes = Math.round((total % 3600) / 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${Math.max(1, minutes)} min`;
};

const AudioItem = ({
  file,
  index,
  selectedAudio,
  editingId,
  editingName,
  hoveredItem,
  deletingId,
  isPlaying,
  onSelect,
  onSelectBook,
  onHover,
  onRename,
  onCancelRename,
  onDelete,
  onCancelDelete,
  onStartRename,
  onDeleteClick,
  setEditingName,
  isCurated = false,
  isRemoving = false,
  isOpening = false,
  isDisabled = false
}) => {
  const [coverFailed, setCoverFailed] = useState(false);
  const [coverSrc, setCoverSrc] = useState('');
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [etaMode, setEtaMode] = useState(0);
  // Local split of the stored "Title - Author" name while the rename form is
  // open; recomposed into editingName so the Dashboard rename contract stays
  // a single string.
  const [editTitle, setEditTitle] = useState('');
  const [editAuthor, setEditAuthor] = useState('');

  const isSelected = selectedAudio === index;
  const isHovered = hoveredItem === index;
  const isEditing = editingId === file.id;
  const isDeleting = deletingId === file.id;
  const normalizedStatus = String(file?.status || '').toLowerCase();
  const isReady = ['ready', 'completed'].includes(normalizedStatus);
  const isFailed = normalizedStatus === 'failed';
  const isKnownProgressStatus = ['in_queue', 'processing', 'uploading', 'consolidating', 'tts_complete', 'queued'].includes(normalizedStatus);
  const isInProgress = isKnownProgressStatus || (Boolean(normalizedStatus) && !['ready', 'completed', 'failed'].includes(normalizedStatus));
  const showCenterSpinner = isInProgress || isOpening;
  const isQueueStatus = normalizedStatus === 'in_queue' || normalizedStatus === 'queued';
  const isProcessingStatus = normalizedStatus === 'processing';
  const completedChapters = Number(file?.completedChapters || 0);

  const progressLabelByStatus = {
    in_queue: 'In queue',
    processing: 'Processing...',
    uploading: 'Uploading...',
    consolidating: 'Consolidating...',
    tts_complete: 'Finalizing...',
    queued: 'Queued...'
  };

  // No time estimates on the card (founder, 27 Sep 2026): the pipeline cannot
  // keep a promise like "about half an hour", so the bar shows progress and
  // the phase, never minutes.

  // "Chapter X of Y" is the strongest possible signal that work is actually
  // happening, so it leads the rotation whenever chapter counts are known.
  const totalChapters = Number(file?.totalChapters || 0);
  const chapterFractionLabel = (isProcessingStatus && totalChapters > 0)
    ? `Chapter ${Math.min(completedChapters + 1, totalChapters)} of ${totalChapters}`
    : '';

  // Story Time: there are no chapters to count until the very end (the series is written, then rendered as a whole), so the card says what is
  // happening in plain words. The numbers are the worker's PROGRESS map (backend/app/services/story_time/worker_job.py).
  const isStoryTime = String(file?.documentType || '').trim().toLowerCase() === 'story_time';
  const [previewOpen, setPreviewOpen] = useState(false);
  const storyPhaseLabel = (() => {
    const p = Number(file?.progress || 0);
    if (p < 5) return 'Waiting for a writer';
    if (p < 30) return 'Writing your story';
    if (p < 45) return 'Setting up the studio';
    if (p < 65) return 'Recording the cast';
    if (p < 80) return 'Adding sound';
    if (p < 92) return 'Scoring the music';
    return 'Mixing the episodes';
  })();

  const processingLabels = (() => {
    if (!isProcessingStatus) return [];
    if (isStoryTime) return [storyPhaseLabel]; // no time promise: the pipeline cannot keep one
    const labels = [];
    if (chapterFractionLabel) labels.push(chapterFractionLabel);
    if (completedChapters > 0) labels.push('1st chapter ready');
    return labels.length ? labels : ['Converting'];
  })();

  useEffect(() => {
    if (processingLabels.length < 2) {
      setEtaMode(0);
      return undefined;
    }
    const timerId = window.setInterval(() => {
      setEtaMode((prev) => (Number(prev) + 1) % processingLabels.length);
    }, 3200);
    return () => window.clearInterval(timerId);
  }, [isProcessingStatus, processingLabels.length]);

  // Load bar: how far along the conversion is, 0..100, or null when nothing
  // measurable is known yet (queued, first seconds) and the bar just shimmers.
  const loadPercent = (() => {
    const p = Number(file?.progress);
    if (isStoryTime) return Number.isFinite(p) && p > 0 ? Math.min(99, Math.max(3, p)) : null;
    if (totalChapters > 0 && isProcessingStatus) {
      return Math.min(99, Math.max(4, Math.round((completedChapters / totalChapters) * 100)));
    }
    if (Number.isFinite(p) && p > 0 && p <= 100) return Math.min(99, Math.max(3, p));
    return null;
  })();

  const inProgressLabel = (() => {
    if (isQueueStatus) return 'In queue';
    if (isProcessingStatus) {
      return processingLabels[Number(etaMode) % processingLabels.length] || 'Processing...';
    }
    return progressLabelByStatus[normalizedStatus] || 'Processing...';
  })();

  const seasonCount = file?.seasons?.length || 0;
  const { title, author } = splitTitleAndAuthor(seasonCount > 1 ? String(file.name || '').replace(/:\s*Season \d+$/, '') : file.name);
  const normalizedDocType = String(file?.documentType || '').trim().toLowerCase();
  const isBookLike = isCurated || !normalizedDocType || normalizedDocType === 'book';
  const fallbackTypeLabel = normalizedDocType === 'story_time' ? 'Story Time' : (isBookLike ? 'Book' : 'Document');

  const coverUrl = String(file?.coverUrl || '').trim();
  // The small copy (480 px wide) is what a card needs; the full cover is the
  // fallback when the small one does not exist yet.
  const coverThumbUrl = String(file?.coverThumbUrl || '').trim();
  const [thumbFailed, setThumbFailed] = useState(false);
  const usingThumb = Boolean(coverThumbUrl) && !thumbFailed;
  const coverTarget = usingThumb ? coverThumbUrl : coverUrl;
  const shouldShowCover = Boolean(coverSrc) && !coverFailed;
  const normalizedPlaybackState = String(file?.playbackState || '').toLowerCase();
  const playbackPercent = Math.max(0, Math.min(100, Number(file?.playbackPercent || 0)));
  const playbackBadge = (() => {
    if (normalizedPlaybackState === 'read' || playbackPercent >= 99.5) return 'Read';
    if (normalizedPlaybackState === 'in_progress' || playbackPercent >= 0.5) {
      return `${Math.max(1, Math.round(playbackPercent))}%`;
    }
    return 'New';
  })();
  const showNewCornerBadge = isReady && !isCurated && playbackBadge === 'New';
  // Story Time Premium: first episode free, the rest need a subscription.
  const isPremium = Boolean(file?.isPremium);

  // Seed the rename form fields when editing opens.
  useEffect(() => {
    if (isEditing) {
      const parsed = splitTitleAndAuthor(editingName);
      setEditTitle(parsed.title);
      setEditAuthor(parsed.author);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, file.id]);

  const handleEditField = (nextTitle, nextAuthor) => {
    setEditTitle(nextTitle);
    setEditAuthor(nextAuthor);
    setEditingName(composeTitleAndAuthor(nextTitle, nextAuthor));
  };

  const editKeyHandler = (e) => {
    if (e.key === 'Enter') onRename(file.id);
    if (e.key === 'Escape') onCancelRename();
  };

  useEffect(() => {
    let mounted = true;
    let objectUrlToRevoke = '';

    const resolveCover = async () => {
      if (!coverTarget) {
        setCoverSrc('');
        setCoverFailed(false);
        return;
      }

      try {
        const resolved = await wasabiService.resolveCoverImageSource(coverTarget);
        if (!mounted) {
          if (resolved.shouldRevoke && resolved.src) {
            URL.revokeObjectURL(resolved.src);
          }
          return;
        }
        setCoverSrc(resolved.src || '');
        setCoverFailed(false);
        objectUrlToRevoke = resolved.shouldRevoke ? resolved.src : '';
      } catch (error) {
        if (mounted) {
          setCoverSrc('');
          setCoverFailed(true);
        }
      }
    };

    resolveCover();

    return () => {
      mounted = false;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [coverTarget, file?.id]);

  useEffect(() => { setThumbFailed(false); }, [coverThumbUrl, file?.id]);
  useEffect(() => { setCoverLoaded(false); }, [coverSrc]);

  return (
    <div
      className={`${styles.audioItem} ${isSelected ? styles.active : ''} ${isInProgress ? styles.processing : ''} ${isRemoving ? styles.removing : ''} ${isDisabled ? styles.disabled : ''}`}
      style={{ animationDelay: `${Math.min(index ?? 0, 12) * 0.04}s` }}
      // Phones (no hover): a tap opens the preview sheet, whose Play starts it; the playing title's tile still toggles playback (2 Oct 2026)
      // A finished title opens its sheet (synopsis, Play, more); the play chip on the cover plays it straight away (founder, 2 Oct 2026)
      onClick={() => { if (isDisabled) return; if (isReady && !isCurated) setPreviewOpen(true); else onSelect(index); }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
    >
      <div className={styles.cover}>
        {/* The drawn cover sits underneath until the real one has loaded, so a
            slow or failed image never shows as a broken-image icon. */}
        {!(shouldShowCover && coverLoaded) && (
          <AutoBookCover
            title={title}
            author={author}
            typeLabel={isBookLike ? '' : fallbackTypeLabel}
            seed={title || file?.id}
          />
        )}
        {shouldShowCover && (
          <img
            src={coverSrc}
            alt=""
            className={styles.coverImage}
            style={coverLoaded ? undefined : { position: 'absolute', opacity: 0, pointerEvents: 'none' }}
            loading="lazy"
            decoding="async"
            onLoad={() => setCoverLoaded(true)}
            onError={() => {
              if (usingThumb) {
                // No small copy yet: show the full cover now, and have one made for next time.
                setThumbFailed(true);
                wasabiService.requestCoverThumb(coverUrl);
              } else {
                setCoverFailed(true);
              }
            }}
          />
        )}
        <div className={styles.coverGradient} />
        {showNewCornerBadge && (
          <span className={styles.newCornerBadge}>NEW</span>
        )}
        {isPremium && (
          <span className={styles.premiumCornerBadge} title="Episode 1 is free; the rest come with a subscription">
            PREMIUM
          </span>
        )}

        {showCenterSpinner ? (
          <RiLoader4Line size={30} className={`${styles.centerIcon} spin`} />
        ) : (
          isSelected && isPlaying ? (
            <div className={styles.playIcon} role="button" aria-label="Pause" onClick={(e) => { e.stopPropagation(); if (!isDisabled) onSelect(index); }}>
              <RiPauseFill size={28} />
            </div>
          ) : null  /* the play chip is gone (founder, 4 Oct 2026): a tap opens the sheet, whose Play starts it; only the playing title keeps a pause chip */
        )}

        {!isCurated && (
          <div className={`${styles.actions} ${isDeleting ? styles.actionsConfirming : ''}`} onClick={e => e.stopPropagation()}>
            {!isEditing && (isHovered || isDeleting) && (
              <>
                {isDeleting ? (
                  <div className={styles.deleteConfirm}>
                    <span>Delete?</span>
                    <button onClick={() => onDelete(file.id)} className={styles.btnDanger}>
                      Yes
                    </button>
                    <button onClick={onCancelDelete} className={styles.btnSecondary}>
                      No
                    </button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => onStartRename(file)} className={styles.actionBtn} title="Rename">
                      <RiPencilLine size={17} />
                    </button>
                    <button
                      onClick={(e) => onDeleteClick(e, file.id)}
                      className={`${styles.actionBtn} ${styles.delete}`}
                      title="Delete"
                    >
                      <RiDeleteBinLine size={17} />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.content}>
        {isEditing ? (
          <div className={styles.editForm}>
            <div className={styles.editFormInner}>
              <input
                type="text"
                value={editTitle}
                placeholder="Title"
                aria-label="Title"
                onChange={(e) => handleEditField(e.target.value, editAuthor)}
                onKeyDown={editKeyHandler}
                className={styles.editInput}
                autoFocus
                onClick={e => e.stopPropagation()}
              />
              <input
                type="text"
                value={editAuthor}
                placeholder="Author (optional)"
                aria-label="Author"
                onChange={(e) => handleEditField(editTitle, e.target.value)}
                onKeyDown={editKeyHandler}
                className={styles.editInput}
                onClick={e => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(file.id);
                }}
                className={styles.btnPrimary}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.titleContainer}>
              <div className={styles.title}>{title}</div>
              {author && <div className={styles.author}>{author}</div>}
            </div>
            <div className={styles.metadata}>
              {isInProgress ? (
                <div className={styles.loadRow}>
                  <div
                    className={`${styles.loadBar} ${loadPercent === null ? styles.loadBarWaiting : ''}`}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={loadPercent === null ? undefined : Math.round(loadPercent)}
                    aria-label={inProgressLabel}
                  >
                    <span className={styles.loadFill} style={{ width: `${loadPercent === null ? 100 : loadPercent}%` }} />
                    <span className={styles.loadLabel}>{inProgressLabel}</span>
                  </div>
                  {!isCurated && <PushOptIn compact size={26} />}
                </div>
              ) : isOpening ? (
                <span className={`${styles.badge} byopdf-pulse`}>Opening…</span>
              ) : isFailed ? (
                <span
                  className={`${styles.badge} ${styles.failedBadge}`}
                  title="This conversion failed and your credits were returned. Create the book again from New Book, or reply to the failure email for help."
                >
                  Failed · credits returned
                </span>
              ) : (isCurated || isReady) ? (
                (!showNewCornerBadge || playbackBadge !== 'New') && (
                  <span className={styles.badge}>{playbackBadge}</span>
                )
              ) : file.heard ? (
                <span className={styles.badge}>
                  <RiCheckDoubleLine size={14} />
                  Heard
                </span>
              ) : null}
              <div className={styles.metadataRow}>
                <span className={styles.duration}>
                  {seasonCount > 1 ? (file.seasonInProgress ? `Season ${file.seasonInProgress} coming` : `${seasonCount} seasons`) : formatDuration(file.totalDuration || 0)}
                </span>
                {(isCurated || isReady) && !isPremium && (
                  <span className={styles.offlineSlot}>
                    <OfflineButton book={file} isCurated={isCurated} size={28} />
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {previewOpen && (
        <LibraryPreview file={file} coverSrc={coverSrc} onClose={() => setPreviewOpen(false)} onPlay={() => !isDisabled && onSelect(index)}
          onPlayBook={onSelectBook} />
      )}
    </div>
  );
};

export default AudioItem;
